import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useUserProfile } from '@/hooks/use-user-profile';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import LoadingFallback from '@/components/LoadingFallback';

const EmailVerified: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user, isLoading: authLoading } = useAuth();
  const { profile, isLoading: profileLoading, refetch } = useUserProfile();
  const [isSettingUpOrg, setIsSettingUpOrg] = useState(false);
  const [orgSetupError, setOrgSetupError] = useState('');

  useEffect(() => {
    // Redirect to auth if not authenticated (after loading is complete)
    if (!authLoading && !isAuthenticated) {
      navigate('/auth');
      return;
    }

    // Auto-setup organization if user has organization data in metadata
    const handleAutoOrgSetup = async () => {
      if (!user?.user_metadata?.organization_data || profile?.organization_id || isSettingUpOrg) {
        return;
      }

      setIsSettingUpOrg(true);
      setOrgSetupError('');

      try {
        const orgData = user.user_metadata.organization_data;
        
        if (orgData.name) {
          // Create new organization
          const { data, error } = await supabase.rpc('create_user_organization', {
            organization_name: orgData.name.trim()
          });

          if (error) throw error;

          const result = data as any;
          if (result?.error) {
            throw new Error(result.error);
          }
        } else if (orgData.id) {
          // Join existing organization using secure RPC
          const { data: joinResult, error: joinError } = await supabase.rpc('join_organization_by_id', {
            target_org_id: orgData.id.trim()
          });

          if (joinError) {
            throw new Error(joinError.message || 'Failed to join organization');
          }

          const joinData = joinResult as any;
          if (joinData?.error) {
            throw new Error(joinData.error);
          }
        }

        // Refresh profile after organization setup
        await refetch();
        
        // Navigate to admin after successful setup
        setTimeout(() => {
          navigate('/admin');
        }, 1000);

      } catch (error: any) {
        console.error('Organization setup error:', error);
        setOrgSetupError(error.message || 'Failed to set up organization');
      } finally {
        setIsSettingUpOrg(false);
      }
    };

    handleAutoOrgSetup();
  }, [authLoading, isAuthenticated, user, profile, isSettingUpOrg, navigate, refetch]);

  useEffect(() => {
    // Wait for loading to complete and auto-setup to finish
    if (authLoading || profileLoading || isSettingUpOrg) {
      return;
    }

    // Redirect if user already has organization
    if (profile?.organization_id) {
      setTimeout(() => {
        navigate('/admin');
      }, 1000);
      return;
    }
    
    // Redirect to organization setup if no organization and no metadata
    if (!profile?.organization_id && !user?.user_metadata?.organization_data) {
      // Give a moment for the page to show the verified message
      setTimeout(() => {
        navigate('/organization-setup');
      }, 2000);
    }
  }, [authLoading, profileLoading, profile, isSettingUpOrg, user, navigate]);

  // Show loading while auth is initializing
  if (authLoading || profileLoading) {
    return <LoadingFallback message="Loading your account..." />;
  }

  const handleContinue = () => {
    if (profile?.organization_id) {
      navigate('/admin');
    } else {
      navigate('/organization-setup');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-secondary/10 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100">
            <CheckCircle className="h-6 w-6 text-emerald-600" />
          </div>
          <CardTitle className="text-xl font-semibold">Email Verified!</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-center text-muted-foreground">
            Your email address has been successfully verified.
          </p>
          
          {isSettingUpOrg && (
            <div className="flex items-center justify-center space-x-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Setting up your organization...</span>
            </div>
          )}

          {orgSetupError && (
            <Alert variant="destructive">
              <AlertDescription>{orgSetupError}</AlertDescription>
            </Alert>
          )}
          
          {!authLoading && !profileLoading && !isSettingUpOrg && (
            <div className="space-y-3">
              {profile?.organization_id ? (
                <p className="text-center text-sm">
                  Welcome! Your organization is set up and you can now access your dashboard.
                </p>
              ) : (
                <div className="space-y-2">
                  <p className="text-center text-sm text-muted-foreground">
                    {user?.user_metadata?.organization_data 
                      ? 'There was an issue setting up your organization.'
                      : 'Next step: Create your organization to get started.'}
                  </p>
                  {orgSetupError ? (
                    <p className="text-center text-xs text-destructive">
                      {orgSetupError}
                    </p>
                  ) : (
                    <p className="text-center text-xs text-muted-foreground">
                      You'll be redirected to organization setup shortly...
                    </p>
                  )}
                </div>
              )}
              
              <Button onClick={handleContinue} className="w-full">
                {profile?.organization_id ? 'Go to Dashboard' : 'Continue to Organization Setup'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailVerified;